// Curation inbox view — reads pending_inbox, ranks by triage urgency, shows
// a diff-style preview. Approve/reject actions invoke an Edge Function that
// performs role-gated authz; the browser never writes pending_changes
// directly (RLS doesn't expose a write policy on it).

import type { InboxItem } from "../lib/types.ts";
import type { Role } from "../lib/auth.ts";
import { approveChange, rejectChange } from "../lib/actions.ts";

const escHtml = (s: string | null | undefined) =>
  String(s ?? "").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!),
  );

const KIND_LABELS = {
  new_topic: "New topic proposal",
  score_dispute: "Score dispute",
  definition_edit: "Definition edit",
  score_update: "Cron score update",
};

export function renderInbox(
  host: HTMLElement,
  items: InboxItem[],
  role: Role,
): () => void {
  host.innerHTML = "";
  if (!items.length) {
    host.innerHTML = `<div class="viz-empty">Inbox is empty. Cron runs will populate it; user disputes too.</div>`;
    return () => { host.innerHTML = ""; };
  }

  const canApprove = role === "approver" || role === "editor";
  const banner = canApprove
    ? `<p class="muted">${items.length} pending item${items.length === 1 ? "" : "s"} — approve or reject below.</p>`
    : `<p class="muted">${items.length} pending item${items.length === 1 ? "" : "s"} — read-only (your role: ${role}).</p>`;

  const rows = items.map((it) => {
    const kindLabel = KIND_LABELS[it.kind] ?? it.kind;
    const author = it.proposed_by_kind === "cron"
      ? "cron"
      : it.proposed_by_kind === "ai"
      ? "AI"
      : "user";
    const before = it.before_state ? `<pre class="diff before">${escHtml(JSON.stringify(it.before_state, null, 2))}</pre>` : "";
    const after = it.after_state ? `<pre class="diff after">${escHtml(JSON.stringify(it.after_state, null, 2))}</pre>` : "";
    const actions = canApprove
      ? `<div class="inbox-actions">
           <button class="btn-approve" data-id="${it.id}">Approve</button>
           <button class="btn-reject"  data-id="${it.id}">Reject</button>
         </div>`
      : "";
    return `
      <article class="inbox-item kind-${it.kind} ${it.is_stale ? "stale" : ""}" data-id="${it.id}">
        <header class="inbox-head">
          <span class="kind-chip kind-${it.kind}">${escHtml(kindLabel)}</span>
          <span class="muted">${author} · ${escHtml(it.topic_name ?? "(no topic)")}</span>
          ${it.is_stale ? `<span class="stale-tag" title="Pending >30 days">stale</span>` : ""}
        </header>
        ${it.rationale ? `<p class="rationale">${escHtml(it.rationale)}</p>` : ""}
        <div class="diff-pair">${before}${after}</div>
        ${actions}
      </article>`;
  }).join("");

  host.innerHTML = `
    <div class="inbox-wrap">
      ${banner}
      ${rows}
    </div>
  `;

  // Wire approve/reject. Disable everything except the role-allowed buttons,
  // and re-fetch the inbox after a successful action so the row drops away.
  if (canApprove) {
    host.querySelectorAll<HTMLButtonElement>(".btn-approve, .btn-reject").forEach((btn) => {
      btn.addEventListener("click", async () => {
        const id = Number(btn.dataset.id);
        const isApprove = btn.classList.contains("btn-approve");
        const note = isApprove
          ? prompt("Optional note (visible in audit trail):") ?? undefined
          : prompt("Reason for rejection (required for transparency):") ?? undefined;
        if (!isApprove && !note) return;  // require a note for rejection
        const card = btn.closest<HTMLElement>(".inbox-item");
        if (card) card.style.opacity = "0.5";
        // Mark the buttons busy so a second click can't fire mid-flight.
        host.querySelectorAll<HTMLButtonElement>(`[data-id="${id}"]`).forEach((b) => { b.disabled = true; });
        try {
          if (isApprove) await approveChange(id, note);
          else await rejectChange(id, note);
          // Optimistic remove. Caller can re-render fresh by re-invoking.
          card?.remove();
        } catch (e) {
          alert(`Action failed: ${String(e)}`);
          if (card) card.style.opacity = "1";
          host.querySelectorAll<HTMLButtonElement>(`[data-id="${id}"]`).forEach((b) => { b.disabled = false; });
        }
      });
    });
  } else {
    host.querySelectorAll<HTMLButtonElement>(".btn-approve, .btn-reject").forEach((btn) => {
      btn.disabled = true;
      btn.title = `Your role (${role}) cannot approve or reject. Ask an editor.`;
    });
  }

  return () => { host.innerHTML = ""; };
}
